// ---------------------------------------------------------------------------
// The app root: a public page as it is, anything else behind the guard (R12c)
//
// Which routes are public is decided in one place, with a reason per route
// (public-routes.ts). It lived in client/startup.ts as an inline chain that
// nothing could reach, and `optOut` was missing from it — so the unsubscribe
// link in every outbound email landed a prospect on a login form.
//
// This is a pure wiring function, so a test can pass a fake guard
// (tests/dom/router.dom.ts). The app passes the session guard over REST
// (auth/session-guard.ts, R13, decision 7.16); it was Adornis's
// authenticationGuard until then.
//
// The outlet stays a function the guard calls, so the page's remount (in the
// outlet, router/remount.ts) sits inside the guard and never clears it. Every
// guarded route renders the same template, so the guard lives on across page
// changes; only a switch between public and guarded makes a new one, as
// before.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { isPublicRoute } from './public-routes.js';

export interface AppRootInput {
  /** The current route. */
  state: { readonly name: string };
  /** Renders the page for that route, in its frame. */
  outlet: () => TemplateResult;
  /** Renders the outlet once there is a user, a sign-in until then. */
  guard: (outlet: () => TemplateResult) => unknown;
}

export function appRoot({ state, outlet, guard }: AppRootInput): TemplateResult {
  if (isPublicRoute(state.name)) return html`${outlet()}`;
  return html`${guard(outlet)}`;
}
